// useActiveCall(repId) hook (R10).
//
// Subscribes to the active-call SSE stream for the signed-in rep and
// returns the current active-call snapshot. The six write-heavy
// review screens (CurrentActionReview, BuildOrderReview, PaymentReview,
// SubscriptionsReview, ColicCoverageReview, GiftsReview) consume this
// hook and feed its result into their `companionSignals.midCall` prop
// so the DataBoundary swaps into the mid-call layout while the rep is
// on a call.
//
// The hook is intentionally lazy about the HubSpot session: when
// `repId` is null (session still pending) it stays idle and does not
// open an EventSource. The caller can pass the client as null in tests
// to skip the subscription entirely.
//
// This hook holds NO business logic beyond driving the subscription;
// screens still own the `label` they want to display for the
// mid-call signal.

import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ActiveCallEvent,
  ActiveCallEventPayload,
  ActiveCallStreamClient,
} from '../api/active-call-stream.js';

export interface UseActiveCallOptions {
  // Only count the mid-call signal when the active call's `customerId`
  // matches this id. When null, every active call for the rep flips the
  // signal on. The six review screens pass the customerId they are
  // currently viewing so a call to one customer never swaps the layout
  // on a tab the rep opened for a different one.
  customerIdForFilter?: string | null;
}

export interface UseActiveCallResult {
  activeCall: ActiveCallEventPayload | null;
  // Convenience: true when `activeCall` is set AND passes the
  // customerIdForFilter check (if supplied). Screens use this directly.
  isActiveForCustomer: boolean;
  // Set when the subscription itself errored, not when a call ended.
  streamError: Error | null;
}

export function useActiveCall(
  client: ActiveCallStreamClient | null,
  args: { projectId: string | null; repId: string | null } | null,
  options: UseActiveCallOptions = {},
): UseActiveCallResult {
  const [activeCall, setActiveCall] = useState<ActiveCallEventPayload | null>(null);
  const [streamError, setStreamError] = useState<Error | null>(null);
  const mountedRef = useRef<boolean>(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const projectId = args?.projectId ?? null;
  const repId = args?.repId ?? null;

  useEffect(() => {
    setActiveCall(null);
    setStreamError(null);
    if (!client || !projectId || !repId) return undefined;

    function onEvent(event: ActiveCallEvent): void {
      if (!mountedRef.current) return;
      switch (event.type) {
        case 'call_started':
        case 'call_in_progress': {
          setActiveCall(event.payload);
          return;
        }
        case 'call_ended': {
          setActiveCall((prev) =>
            prev && prev.callId === event.payload.callId ? null : prev);
          return;
        }
        default:
          return;
      }
    }

    function onError(err: Error): void {
      if (!mountedRef.current) return;
      setStreamError(err);
    }

    const unsubscribe = client.subscribe({ projectId, repId }, onEvent, onError);
    return () => {
      try { unsubscribe(); } catch { /* ignore */ }
    };
  }, [client, projectId, repId]);

  const isActiveForCustomer = useMemo(() => {
    if (!activeCall) return false;
    const filter = options.customerIdForFilter ?? null;
    if (filter === null) return true;
    return activeCall.customerId === filter;
  }, [activeCall, options.customerIdForFilter]);

  return { activeCall, isActiveForCustomer, streamError };
}
